import { UserAvatar } from "components/molecules";
import React from "react";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import { PaziaButton, PaziaLoader } from "components/atoms";
import { formatDate } from "utils";
import { UserNoteModal } from "components/templates/UserNoteModal/UserNoteModal";
import { useStudentNotes } from "api/hooks/useStudentNotes";
import { PaziaBox } from "components/atoms";
import { studentsApi } from "api/studentsApi";
import { useToaster } from "utils";

interface StudentNotesProps {
  studentId: number;
  className?: string;
  inline?: boolean;
}

export const StudentNotes = ({ studentId, className = "", inline = false }: StudentNotesProps) => {
  const [noteModalId, setNoteModalId] = React.useState<number | null>(null);
  const [editingNote, setEditingNote] = React.useState<{ id: number; notes: string } | null>(null);
  const { loading, data: notes, refresh: refreshNotes } = useStudentNotes(Number(studentId));
  const { showSuccess, showError } = useToaster();

  if (loading) return <PaziaLoader position="center" />;
  if (!notes) return null;

  const sortedNotes = notes.sort((a, b) => (a.createdDate > b.createdDate ? -1 : 1));

  const handleEdit = (noteId: number, noteText: string) => {
    setEditingNote({ id: noteId, notes: noteText });
    setNoteModalId(studentId);
  };

  const handleDelete = async (noteId: number) => {
    if (window.confirm("Czy na pewno chcesz usunąć tę notatkę?")) {
      try {
        await studentsApi.deleteNote(studentId, noteId);
        showSuccess("Notatka została usunięta");
        refreshNotes();
      } catch (error) {
        showError("Nie udało się usunąć notatki");
      }
    }
  };

  const handleCloseModal = () => {
    setNoteModalId(null);
    setEditingNote(null);
    refreshNotes();
  };

  return (
    <>
      <PaziaBox className={`${className} ${!inline ? "col-span-12 mt-4 lg:col-span-6" : ""}`}>
        <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-3 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Notatki</h2>
          <PaziaButton title="+ Dodaj" type="success2" onClick={() => setNoteModalId(studentId)} />
        </div>
        <div className="scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent max-h-[400px] space-y-2 overflow-y-auto pr-2">
          {sortedNotes.length > 0 ? (
            sortedNotes.map((note) => (
              <div
                className="group flex flex-col rounded-md border border-slate-200 bg-slate-50 p-2 transition-colors hover:border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-slate-600 dark:hover:bg-slate-700/50"
                key={note.id}
              >
                <div className="mb-1 flex items-center justify-between">
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {formatDate(note.createdDate, true, true)}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        onClick={() => handleEdit(note.id, note.notes)}
                        className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                        title="Edytuj notatkę"
                      >
                        <HiOutlinePencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(note.id)}
                        className="rounded p-1 text-slate-500 hover:bg-red-100 hover:text-red-700 dark:text-slate-400 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                        title="Usuń notatkę"
                      >
                        <HiOutlineTrash size={14} />
                      </button>
                    </div>
                    <div className="flex items-center">
                      <UserAvatar firstName={note.author.firstName} surname={note.author.surname} size="small" />
                      <span className="ml-1.5 text-xs text-slate-600 dark:text-slate-400">
                        {note.author.firstName} {note.author.surname}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-sm leading-snug text-slate-700 dark:text-slate-300">{note.notes}</div>
              </div>
            ))
          ) : (
            <div className="py-8 text-center">
              <p className="text-sm italic text-slate-400 dark:text-slate-500">Brak notatek</p>
            </div>
          )}
        </div>
      </PaziaBox>
      <UserNoteModal
        userId={noteModalId}
        noteId={editingNote?.id}
        initialNotes={editingNote?.notes}
        onCloseRequest={handleCloseModal}
      />
    </>
  );
};
